// Guarantee rules (docs/mastermind-admin-plan.md 3.2; named so for owner and admin by Adam's answer of 20 Oct to the
// build plan's question 3): the 4 numbers every member's guarantee check for this mastermind uses. An owner changes
// them (hub.set_accountability_rules, audited with the numbers before and after); an admin reads them. The database
// holds them to their bounds. Before they're saved, the card says whose check the new numbers change (rulesEffect, the
// critique's admin 10); where anyone stands on their accountability doesn't follow these numbers. stored: the numbers
// as hub.accountability_rules holds them since migration 81 (decision 56: out of the product's settings, which members
// read), or null on a database before 81 (the product's settings then).
import { useState } from 'react'
import { Button, Card, Field, TextInput, useAdmin } from '../ui/index.js'
import { MM } from './words.js'
import { setRules } from './data.js'
import { rulesEffect, rulesFrom } from './section.js'

const BOUNDS = { limit: [0, 12], pct: [50, 100], days: [1, 60], catchUp: [1, 60] }

export function Rules({ product, stored = null, role, members = [], onSaved }) {
  const { api } = useAdmin()
  const [rules, setLocal] = useState(() => rulesFrom(product, stored))
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState(null)
  const [busy, setBusy] = useState(false)
  const [line, setLine] = useState(null)
  const owner = role === 'owner'

  const start = () => { setForm({ limit: String(rules.limit), pct: String(rules.pct), days: String(rules.days), catchUp: String(rules.catchUp) }); setEditing(true); setLine(null) }
  const ok = form && Object.entries(BOUNDS).every(([k, [lo, hi]]) => /^\d{1,3}$/.test(form[k]) && +form[k] >= lo && +form[k] <= hi)
  const next = ok ? { ...rules, limit: +form.limit, pct: +form.pct, days: +form.days, catchUp: +form.catchUp } : null
  const changed = next && ['limit', 'pct', 'days', 'catchUp'].some(k => next[k] !== rules[k])
  const effect = changed ? rulesEffect(members, rules, next) : null

  async function save() {
    if (busy) return
    if (!ok) { setLine({ bad: true, text: MM.rulesBad }); return }
    setBusy(true); setLine(null)
    try {
      const saved = await setRules(api, { product: product.id, rules: { absence_limit: +form.limit, actions_pct: +form.pct, goal_days: +form.days, catch_up_days: +form.catchUp } }, { product: product.name })
      setLocal(r => ({ ...r, limit: saved.absence_limit, pct: saved.actions_pct, days: saved.goal_days, catchUp: saved.catch_up_days }))
      setEditing(false)
      setLine({ text: MM.rulesSaved(product.name) })
      onSaved?.()
    } catch (e) {
      setLine({ bad: true, text: e.message })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title={MM.rulesH} className="amx-rules" id="amx-rules"
      actions={owner && !editing ? <Button size="sm" onClick={start}>{MM.change}</Button> : null}>
      {!editing && (
        <ul className="amx-rules-list">
          {MM.rulesLines(rules).map(l => <li key={l}>{l}</li>)}
        </ul>
      )}
      {editing && (
        <div className="amx-rules-form">
          {[['limit', MM.fields.limit(rules.months)], ['pct', MM.fields.pct], ['days', MM.fields.days], ['catchUp', MM.fields.catchUp]].map(([k, label]) => (
            <Field key={k} label={label} hint={MM.bounds[k]} plain>
              {props => <TextInput {...props} value={form[k]} inputMode="numeric" onChange={v => setForm(f => ({ ...f, [k]: v.replace(/[^\d]/g, '').slice(0, 3) }))} className="amx-num" />}
            </Field>
          ))}
          {effect && (
            <section className="amx-rules-effect" aria-labelledby="amx-rules-effect-h">
              <h3 className="amx-h3" id="amx-rules-effect-h">{MM.rulesEffectH}</h3>
              <p className="amx-meta">{MM.rulesEffect(effect.names)}</p>
              {effect.catchUp && <p className="amx-meta">{MM.rulesEffectCatchUp}</p>}
              <p className="amx-muted">{MM.rulesEffectAll}</p>
            </section>
          )}
          <span className="amx-row-actions">
            <Button kind="ink" size="sm" busy={busy} onClick={save}>{MM.saveRules}</Button>
            <Button size="sm" disabled={busy} onClick={() => { setEditing(false); setLine(null) }}>{MM.cancelReview}</Button>
          </span>
        </div>
      )}
      {!owner && <p className="amx-muted">{MM.rulesOwner}</p>}
      {line && <p className={line.bad ? 'ad-error' : 'amx-said'} role={line.bad ? 'alert' : 'status'}>{line.text}</p>}
    </Card>
  )
}
